import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DocumentText1, Calendar, User, Clock, CloseCircle, Edit2 } from "iconsax-react";
import CheckIcon from "@/shared/assets/icons/CheckIcon";
import type { Icon } from "iconsax-react";
import { ModalForm, Button, Prompt, Skeleton, FileViewer, Badge, Toast, type BadgeColor } from "@/shared/ui";
import { FileUploader } from "@/shared/components";
import { extractErrorMessage, formatDateForDisplay, downloadBlob, ensurePdfExtension } from "@/shared/utils";
import { ReviewNoteModal, DetailFieldRow, DetailModalFooterActions } from "@/features/hr/shared";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts";
import { useOrderSigning, RevokeSigningButton } from "@/features/signing";
import {
  useGetAnnualLeaveQuery,
  useGetUnpaidLeaveQuery,
  useGetMedicalLeaveQuery,
  useApproveAnnualLeaveMutation,
  useApproveUnpaidLeaveMutation,
  useApproveMedicalLeaveMutation,
  useRejectAnnualLeaveMutation,
  useRejectUnpaidLeaveMutation,
  useRejectMedicalLeaveMutation,
  usePreviewAnnualOrderMutation,
  usePreviewUnpaidOrderMutation,
  usePreviewMedicalOrderMutation,
  useSignVacationOrderMutation,
  useSignMedicalLeaveOrderMutation,
  useUploadMedicalCertificateMutation,
  usePreviewAnnualApplicationMutation,
  usePreviewUnpaidApplicationMutation,
  usePreviewMedicalApplicationMutation,
  useRevokeAnnualLeaveMutation,
  useRevokeUnpaidLeaveMutation,
  useRevokeMedicalLeaveMutation,
  useRequestRevisionAnnualLeaveMutation,
  useRequestRevisionUnpaidLeaveMutation,
  useRequestRevisionMedicalLeaveMutation,
} from "../api";
import type { LeaveStatus, LeaveType, LeaveApplication, MedicalLeaveResponse } from "../types";
import { getStatusText } from "../consts/statuses";
import { getAvailableActions, filterActionsByPermissions, type LeaveAction } from "../utils/leaveApplicationRules";
import UploadCertificateModal from "./UploadCertificateModal";

interface Props {
  leaveId: number;
  leaveType: LeaveType;
  onClose: () => void;
  onSignSuccess?: () => void;
}

interface LeaveField {
  key: string;
  label: string;
  value: string;
  icon: Icon;
  isStatus?: boolean;
  statusComponent?: React.ReactNode;
}

function getStatusDisplayInfo(status: LeaveStatus): {
  color: BadgeColor;
  translationKey: string;
  icon: React.ReactNode;
} {
  const statusMap: Record<LeaveStatus, { color: BadgeColor; translationKey: string; icon: React.ReactNode }> = {
    draft: {
      color: "info",
      translationKey: "statusLabels.draft",
      icon: <DocumentText1 size={14} color="currentColor" />,
    },
    pending_review: {
      color: "notice",
      translationKey: "statusLabels.pending_review",
      icon: <Clock size={14} color="currentColor" variant="Bold" />,
    },
    signing_pending: {
      color: "notice",
      translationKey: "statusLabels.signing_pending",
      icon: <Clock size={14} color="currentColor" variant="Bold" />,
    },
    app_approved: {
      color: "notice",
      translationKey: "statusLabels.app_approved",
      icon: <Clock size={14} color="currentColor" variant="Bold" />,
    },
    order_pending: {
      color: "notice",
      translationKey: "statusLabels.order_pending",
      icon: <Clock size={14} color="currentColor" variant="Bold" />,
    },
    order_uploaded: {
      color: "positive",
      translationKey: "statusLabels.order_uploaded",
      icon: <CheckIcon />,
    },
    order_acknowledged: {
      color: "positive",
      translationKey: "statusLabels.order_acknowledged",
      icon: <CheckIcon />,
    },
    active: {
      color: "positive",
      translationKey: "statusLabels.active",
      icon: <CheckIcon />,
    },
    completed: {
      color: "gray",
      translationKey: "statusLabels.completed",
      icon: <CheckIcon />,
    },
    cancelled: {
      color: "negative",
      translationKey: "statusLabels.cancelled",
      icon: <CloseCircle size={14} color="currentColor" variant="Bold" />,
    },
  };
  return (
    statusMap[status] || {
      color: "gray",
      translationKey: `statusLabels.${status}`,
      icon: <DocumentText1 size={14} color="currentColor" />,
    }
  );
}

export default function LeaveDetailsModal({ leaveId, leaveType, onClose, onSignSuccess }: Props) {
  const { hasPermission, hasRole } = useAuth();
  const { t } = useTranslation("LeaveApplicationsPage");

  const {
    data: annualLeave,
    isLoading: isLoadingAnnual,
    isFetching: isFetchingAnnual,
  } = useGetAnnualLeaveQuery(leaveId, {
    skip: leaveType !== "annual",
  });
  const {
    data: unpaidLeave,
    isLoading: isLoadingUnpaid,
    isFetching: isFetchingUnpaid,
  } = useGetUnpaidLeaveQuery(leaveId, {
    skip: leaveType !== "unpaid",
  });
  const {
    data: medicalLeave,
    isLoading: isLoadingMedical,
    isFetching: isFetchingMedical,
  } = useGetMedicalLeaveQuery(leaveId, {
    skip: leaveType !== "medical",
  });

  const isLoading = isLoadingAnnual || isLoadingUnpaid || isLoadingMedical;
  const currentLeave = annualLeave || unpaidLeave || medicalLeave || null;

  const [approveAnnual, { isLoading: _isApprovingAnnual }] = useApproveAnnualLeaveMutation();
  const [approveUnpaid, { isLoading: _isApprovingUnpaid }] = useApproveUnpaidLeaveMutation();
  const [approveMedical, { isLoading: _isApprovingMedical }] = useApproveMedicalLeaveMutation();
  const [rejectAnnual, { isLoading: isRejectingAnnual }] = useRejectAnnualLeaveMutation();
  const [rejectUnpaid, { isLoading: isRejectingUnpaid }] = useRejectUnpaidLeaveMutation();
  const [rejectMedical, { isLoading: isRejectingMedical }] = useRejectMedicalLeaveMutation();
  const [previewAnnualOrder, { isLoading: isDownloadingAnnualOrder }] = usePreviewAnnualOrderMutation();
  const [previewUnpaidOrder, { isLoading: isDownloadingUnpaidOrder }] = usePreviewUnpaidOrderMutation();
  const [previewMedicalOrder, { isLoading: isDownloadingMedicalOrder }] = usePreviewMedicalOrderMutation();

  const [signVacationOrder] = useSignVacationOrderMutation();
  const [signMedicalLeaveOrder] = useSignMedicalLeaveOrderMutation();
  const [previewAnnualApp] = usePreviewAnnualApplicationMutation();
  const [previewUnpaidApp] = usePreviewUnpaidApplicationMutation();
  const [previewMedicalApp] = usePreviewMedicalApplicationMutation();
  const [revokeAnnual] = useRevokeAnnualLeaveMutation();
  const [revokeUnpaid] = useRevokeUnpaidLeaveMutation();
  const [revokeMedical] = useRevokeMedicalLeaveMutation();
  const [revisionAnnual, { isLoading: isRevisioningAnnual }] = useRequestRevisionAnnualLeaveMutation();
  const [revisionUnpaid, { isLoading: isRevisioningUnpaid }] = useRequestRevisionUnpaidLeaveMutation();
  const [revisionMedical, { isLoading: isRevisioningMedical }] = useRequestRevisionMedicalLeaveMutation();

  const isRejecting = isRejectingAnnual || isRejectingUnpaid || isRejectingMedical;
  const isRevisioning = isRevisioningAnnual || isRevisioningUnpaid || isRevisioningMedical;
  const isDownloadingOrder = isDownloadingAnnualOrder || isDownloadingUnpaidOrder || isDownloadingMedicalOrder;

  const [prompt, setPrompt] = useState<{ title: string; text: string; variant?: "success" | "error" } | null>(null);
  const { t: tCommon } = useTranslation("Common");
  const orderSigning = useOrderSigning({
    previewFn() {
      if (leaveType === "annual")
        return previewAnnualOrder(leaveId)
          .unwrap()
          .then((r) => r.blob);
      if (leaveType === "unpaid")
        return previewUnpaidOrder(leaveId)
          .unwrap()
          .then((r) => r.blob);
      return previewMedicalOrder(leaveId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature) {
      const orderId = currentLeave?.order?.id;
      if (!orderId) throw new Error("No order ID");
      if (leaveType === "medical") {
        return signMedicalLeaveOrder({ orderId, cms_signature: cmsSignature, leaveId }).unwrap();
      }
      return signVacationOrder({ orderId, cms_signature: cmsSignature, leaveId, leaveType }).unwrap();
    },
    skipFileUpload: true,
    onSuccess() {
      setPrompt({
        title: tCommon("signing.success.title"),
        text: tCommon("signing.success.text"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  const approveSigning = useOrderSigning({
    previewFn() {
      if (leaveType === "annual")
        return previewAnnualApp(leaveId)
          .unwrap()
          .then((r) => r.blob);
      if (leaveType === "unpaid")
        return previewUnpaidApp(leaveId)
          .unwrap()
          .then((r) => r.blob);
      return previewMedicalApp(leaveId)
        .unwrap()
        .then((r) => r.blob);
    },
    signFn(cmsSignature) {
      const approveFn =
        leaveType === "annual" ? approveAnnual : leaveType === "unpaid" ? approveUnpaid : approveMedical;
      return approveFn({ id: leaveId, cms_signature: cmsSignature }).unwrap();
    },
    skipFileUpload: true,
    onSuccess() {
      setPrompt({
        title: t("actions.approveSuccessTitle"),
        text: t("actions.approveSuccessText"),
        variant: "success",
      });
    },
    setPrompt,
    t: tCommon,
  });

  const [showUploadCertificate, setShowUploadCertificate] = useState(false);
  const [showReviewNoteModal, setShowReviewNoteModal] = useState(false);
  const [showRevisionNoteModal, setShowRevisionNoteModal] = useState(false);
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [uploadMedicalCertificate, { isLoading: isUpdatingCertificate }] = useUploadMedicalCertificateMutation();
  const [pendingUploadSuccess, setPendingUploadSuccess] = useState(false);
  const [previewingOrderLeaveId, setPreviewingOrderLeaveId] = useState<number | null>(null);

  useEffect(
    function () {
      setCertificateFile(null);
    },
    [leaveId],
  );

  const isFetching = isFetchingAnnual || isFetchingUnpaid || isFetchingMedical;

  useEffect(() => {
    if (pendingUploadSuccess && !isFetching) {
      setShowUploadCertificate(false);
      setCertificateFile(null);
      setPendingUploadSuccess(false);
      setPrompt({
        title: t("actions.successTitle"),
        text: t("actions.uploadSuccess"),
        variant: "success",
      });
    }
  }, [pendingUploadSuccess, isFetching, t]);

  function handleUploadSuccess() {
    setPendingUploadSuccess(true);
  }

  async function handleUpdateCertificate() {
    if (!certificateFile || isUpdatingCertificate) return;

    try {
      await uploadMedicalCertificate({ id: leaveId, file: certificateFile }).unwrap();
      setCertificateFile(null);
      setPrompt({
        title: t("actions.successTitle"),
        text: t("actions.updateCertificateSuccess"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "messages.updateCertificateError", t),
        variant: "error",
      });
    }
  }

  function handleApprove() {
    approveSigning.handleSign();
  }

  async function handleReject() {
    setShowReviewNoteModal(true);
  }

  async function handleRejectConfirm(note?: string) {
    if (isRejecting) return;

    try {
      const rejectFn = leaveType === "annual" ? rejectAnnual : leaveType === "unpaid" ? rejectUnpaid : rejectMedical;
      await rejectFn({ id: leaveId, note }).unwrap();
      setShowReviewNoteModal(false);
      setPrompt({
        title: t("actions.rejectSuccessTitle"),
        text: t("actions.rejectSuccessText"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "actions.rejectError", t),
        variant: "error",
      });
    }
  }

  async function handleReviewWithNote(note: string) {
    await handleRejectConfirm(note.trim() || undefined);
  }

  function handleRequestRevision() {
    setShowRevisionNoteModal(true);
  }

  async function handleRevisionConfirm(note: string) {
    if (isRevisioning) return;
    try {
      const revisionFn =
        leaveType === "annual" ? revisionAnnual : leaveType === "unpaid" ? revisionUnpaid : revisionMedical;
      await revisionFn({ id: leaveId, note: note.trim() }).unwrap();
      setShowRevisionNoteModal(false);
      setPrompt({
        title: t("messages.revisionSuccess"),
        text: t("messages.revisionSuccessText"),
        variant: "success",
      });
    } catch (error) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "messages.revisionError", t),
        variant: "error",
      });
    }
  }

  async function handleDownloadOrderDocx() {
    if (previewingOrderLeaveId !== null) return;

    try {
      setPreviewingOrderLeaveId(leaveId);
      let blob: Blob;
      let serverFilename: string;
      let fileName: string;
      if (leaveType === "annual") {
        const result = await previewAnnualOrder(leaveId).unwrap();
        blob = result.blob;
        serverFilename = result.filename;
        fileName = t("downloads.annualLeaveOrderFileName", { leaveId });
      } else if (leaveType === "unpaid") {
        const result = await previewUnpaidOrder(leaveId).unwrap();
        blob = result.blob;
        serverFilename = result.filename;
        fileName = t("downloads.unpaidLeaveOrderFileName", { leaveId });
      } else {
        const result = await previewMedicalOrder(leaveId).unwrap();
        blob = result.blob;
        serverFilename = result.filename;
        fileName = t("downloads.medicalLeaveOrderFileName", { leaveId });
      }
      downloadBlob(blob, ensurePdfExtension(serverFilename || fileName));
    } catch (error) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(error, "actions.downloadOrderDocxError", t),
        variant: "error",
      });
    } finally {
      setPreviewingOrderLeaveId(null);
    }
  }

  function handleAction(action: LeaveAction) {
    switch (action) {
      case "approve":
        handleApprove();
        break;
      case "reject":
        handleReject();
        break;
      case "request_revision":
        handleRequestRevision();
        break;
      case "revoke":
        break;
      case "sign_order":
        orderSigning.handleSign();
        break;
      case "download_order_docx":
        handleDownloadOrderDocx();
        break;
      case "upload_certificate":
        handleShowUploadCertificate();
        break;
    }
  }

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) {
      onSignSuccess?.();
    }
  }

  function handleUploadCertificateClose() {
    setShowUploadCertificate(false);
  }

  function handleReviewNoteModalClose() {
    setShowReviewNoteModal(false);
  }

  function handleShowUploadCertificate() {
    setShowUploadCertificate(true);
  }

  function handleRevoke() {
    if (leaveType === "annual") return revokeAnnual(leaveId).unwrap();
    if (leaveType === "unpaid") return revokeUnpaid(leaveId).unwrap();
    return revokeMedical(leaveId).unwrap();
  }

  function filterField(field: LeaveField): boolean {
    return !!(field.value || field.isStatus);
  }

  function renderField(field: LeaveField, index: number, array: LeaveField[]) {
    return (
      <DetailFieldRow
        key={field.key}
        label={field.label}
        value={field.value}
        icon={field.icon}
        isStatus={field.isStatus}
        statusComponent={field.statusComponent}
        isLast={index === array.length - 1}
      />
    );
  }

  function renderLeaveContent(leave: NonNullable<typeof currentLeave>) {
    const status = leave.status as LeaveStatus;

    const leaveApplication: LeaveApplication = {
      id: leave.id,
      leave_type: leaveType as Exclude<LeaveType, "business_trip">,
      status: leave.status,
      worker: leave.worker,
      start_date: leave.start_date,
      end_date: leave.end_date,
      days_count: leave.days_count,
      created_at: leave.created_at,
      reason: "reason" in leave ? (leave.reason as string | null) : null,
      diagnosis: "diagnosis" in leave ? (leave.diagnosis as string | null) : null,
      certificate_pdf_url:
        "certificate_pdf_url" in leave ? (leave.certificate_pdf_url as string | null) : null,
      certificate_required:
        "certificate_required" in leave ? (leave.certificate_required as boolean) : null,
      application_review_note: leave.application_review_note ?? null,
      destination: null,
      order: leave.order
        ? {
            id: leave.order.id,
            order_number: leave.order.order_number,
            signed_pdf_key: leave.order.signed_pdf_key,
          }
        : null,
    };

    function getStatusBadge(leaveStatus: LeaveStatus) {
      const hasRevision = leaveApplication.application_review_note;
      if (leaveStatus === "draft" && hasRevision) {
        return (
          <Badge
            variant="soft"
            color="notice"
            text={t("statusLabels.revision_requested")}
            icon={<Edit2 size={14} color="currentColor" />}
          />
        );
      }
      const info = getStatusDisplayInfo(leaveStatus);
      const statusText = getStatusText(leaveApplication, t);
      return <Badge variant="soft" color={info.color} text={statusText} icon={info.icon} />;
    }

    const fields: LeaveField[] = [
      {
        key: "worker",
        label: t("details.worker"),
        value: leave.worker.full_name || "",
        icon: User,
      },
      {
        key: "startDate",
        label: t("details.startDate"),
        value: leave.start_date ? formatDateForDisplay(leave.start_date, false) : "",
        icon: Calendar,
      },
      {
        key: "endDate",
        label: t("details.endDate"),
        value: leave.end_date ? formatDateForDisplay(leave.end_date, false) : "",
        icon: Calendar,
      },
      {
        key: "daysCount",
        label: t("details.daysCount"),
        value: String(leave.days_count || 0),
        icon: Calendar,
      },
    ];

    if (leaveType === "annual" && "reason" in leave && leave.reason) {
      fields.push({
        key: "reason",
        label: t("details.reason"),
        value: leave.reason,
        icon: DocumentText1,
      });
    }

    if (leaveType === "unpaid" && "reason" in leave) {
      fields.push({
        key: "reason",
        label: t("details.reason"),
        value: leave.reason || "",
        icon: DocumentText1,
      });
      if (
        "approval_resolution_display" in leave &&
        leave.approval_resolution_display &&
        typeof leave.approval_resolution_display === "string"
      ) {
        fields.push({
          key: "approvalResolution",
          label: t("details.approvalResolution"),
          value: leave.approval_resolution_display,
          icon: DocumentText1,
        });
      }
    }

    if (leaveType === "medical" && "diagnosis" in leave && leave.diagnosis) {
      fields.push({
        key: "diagnosis",
        label: t("details.diagnosis"),
        value: leave.diagnosis,
        icon: DocumentText1,
      });
    }

    fields.push({
      key: "status",
      label: t("details.status"),
      value: "",
      icon: DocumentText1,
      isStatus: true,
      statusComponent: getStatusBadge(status),
    });

    if ("application_review_note" in leave && leave.application_review_note) {
      fields.push({
        key: "revisionNote",
        label: t("details.revisionNote"),
        value: leave.application_review_note as string,
        icon: DocumentText1,
      });
    }

    return (
      <>
        <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
          <h4 className="text-display-2xs content-base-primary">{t("details.title")}</h4>
        </div>

        <div className="flex-1 p-1">
          <div className="flex flex-col py-4">
            <div className="flex flex-col gap-2 mb-4 border-b surface-base-stroke pb-3">
              <p className="text-body-bold-lg content-base-primary">
                {leaveType === "annual"
                  ? t("details.annualLeave")
                  : leaveType === "unpaid"
                    ? t("details.unpaidLeave")
                    : t("details.medicalLeave")}
              </p>
            </div>

            <div className="flex flex-col">
              {fields.filter(filterField).map(function (field, index, array) {
                return renderField(field, index, array);
              })}

              {"application_document" in leave &&
                leave.application_document &&
                (leave.application_document.signed_url || leave.application_document.original_url) && (
                  <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke mt-2">
                    <span className="text-body-regular-md content-base-secondary">
                      {t("details.applicationFileLabel")}
                    </span>
                    <FileViewer
                      existingFileUrl={
                        (leave.application_document.signed_url ||
                          leave.application_document.original_url) as string
                      }
                      secondaryFileUrl={
                        leave.application_document.signed_url && leave.application_document.original_url
                          ? leave.application_document.original_url
                          : undefined
                      }
                    />
                  </div>
                )}

              {"order_document" in leave &&
                leave.order_document &&
                (leave.order_document.signed_url || leave.order_document.original_url) && (
                  <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke mt-2">
                    <span className="text-body-regular-md content-base-secondary">{t("details.orderFileLabel")}</span>
                    <FileViewer
                      existingFileUrl={
                        (leave.order_document.signed_url || leave.order_document.original_url) as string
                      }
                      secondaryFileUrl={
                        leave.order_document.signed_url && leave.order_document.original_url
                          ? leave.order_document.original_url
                          : undefined
                      }
                    />
                  </div>
                )}

              {leaveType === "medical" &&
                "certificate_pdf_url" in leave &&
                (leave as MedicalLeaveResponse).certificate_pdf_url && (
                  <div className="py-3 flex flex-col gap-3 border-t surface-base-stroke mt-2">
                    <span className="text-body-regular-md content-base-secondary">
                      {t("details.certificateFileLabel")}
                    </span>
                    {hasPermission(PERMISSIONS.HR.APPROVE_LEAVE) ? (
                      <>
                        <FileUploader
                          accept=".pdf"
                          maxSizeMB={10}
                          value={certificateFile}
                          existingFileUrl={(leave as MedicalLeaveResponse).certificate_pdf_url}
                          onChange={setCertificateFile}
                          namespace="LeaveApplicationsPage"
                        />
                        {certificateFile && (
                          <Button
                            variant="primary"
                            size="md"
                            className="w-full"
                            disabled={isUpdatingCertificate}
                            onClick={handleUpdateCertificate}>
                            {isUpdatingCertificate ? t("actions.uploading") : t("actions.updateCertificate")}
                          </Button>
                        )}
                      </>
                    ) : (
                      <FileViewer existingFileUrl={(leave as MedicalLeaveResponse).certificate_pdf_url} />
                    )}
                  </div>
                )}

              {status === "order_acknowledged" && (
                <div className="py-5 border-t surface-base-stroke mt-2">
                  <Toast
                    color="grey"
                    text={t("statusMessages.orderAcknowledgedInfo")}
                    closable={false}
                    autoClose={false}
                    isFullWidth
                  />
                </div>
              )}
            </div>
          </div>
        </div>

      </>
    );
  }

  function renderFooter() {
    if (isLoading || !currentLeave) return undefined;

    const leave = currentLeave;

    const leaveApplication: LeaveApplication = {
      id: leave.id,
      leave_type: leaveType as Exclude<LeaveType, "business_trip">,
      status: leave.status,
      worker: leave.worker,
      start_date: leave.start_date,
      end_date: leave.end_date,
      days_count: leave.days_count,
      created_at: leave.created_at,
      reason: "reason" in leave ? (leave.reason as string | null) : null,
      diagnosis: "diagnosis" in leave ? (leave.diagnosis as string | null) : null,
      certificate_pdf_url: "certificate_pdf_url" in leave ? (leave.certificate_pdf_url as string | null) : null,
      certificate_required: "certificate_required" in leave ? (leave.certificate_required as boolean) : null,
      application_review_note: leave.application_review_note ?? null,
      destination: null,
      order: leave.order
        ? {
            id: leave.order.id,
            order_number: leave.order.order_number,
            signed_pdf_key: leave.order.signed_pdf_key,
          }
        : null,
    };

    const { actions: allActions } = getAvailableActions(
      leaveApplication,
      {
        isDownloadingOrder,
        isUpdatingCertificate,
        isRevisioning,
      },
      t,
    );

    const actions = filterActionsByPermissions(allActions, hasPermission, hasRole);

    if (actions.length === 0) return undefined;

    const buttonSize: "lg" | "huge" = actions.length >= 4 ? "lg" : "huge";

    function renderActionButton(action: (typeof actions)[number]) {
      if (action.id === "revoke") {
        return <RevokeSigningButton key="revoke" size={buttonSize} onRevoke={handleRevoke} onSuccess={onClose} />;
      }

      const isDisabled =
        (action.id === "approve" && approveSigning.isLoading) ||
        (action.id === "reject" && isRejecting) ||
        (action.id === "request_revision" && isRevisioning) ||
        (action.id === "sign_order" && orderSigning.isLoading) ||
        (action.id === "download_order_docx" && isDownloadingOrder);

      return (
        <Button
          key={action.id}
          variant={action.variant}
          size={buttonSize}
          className="w-full"
          disabled={isDisabled}
          onClick={() => handleAction(action.id)}>
          {action.icon}
          {action.label}
        </Button>
      );
    }

    return (
      <DetailModalFooterActions
        actions={actions}
        renderButton={function renderButton(action) {
          return renderActionButton(action as (typeof actions)[number]);
        }}
      />
    );
  }

  return (
    <>
      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
        />
      )}

      {previewingOrderLeaveId !== null && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.downloadingOrder")}
          additionalText={t("messages.loader.pleaseWait")}
          namespace="LeaveApplicationsPage"
        />
      )}

      {isUpdatingCertificate && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={t("messages.loader.uploadingCertificate")}
          additionalText={t("messages.loader.pleaseWait")}
          namespace="LeaveApplicationsPage"
        />
      )}

      {orderSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${orderSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {approveSigning.isLoading && (
        <Prompt
          loaderMode={true}
          onClose={() => {}}
          title=""
          text=""
          loaderText={tCommon(`signing.phases.${approveSigning.phase}`)}
          additionalText={tCommon("signing.phases.pleaseWait")}
          namespace="Common"
        />
      )}

      {showUploadCertificate && leaveType === "medical" && (
        <UploadCertificateModal
          leaveId={leaveId}
          onClose={handleUploadCertificateClose}
          onSuccess={handleUploadSuccess}
        />
      )}

      {showReviewNoteModal && (
        <ReviewNoteModal
          action="reject"
          onClose={handleReviewNoteModalClose}
          onSubmit={handleReviewWithNote}
          isSubmitting={isRejecting}
          namespace="LeaveApplicationsPage"
          translationPrefix="review"
        />
      )}

      {showRevisionNoteModal && (
        <ReviewNoteModal
          action="revision"
          onClose={() => setShowRevisionNoteModal(false)}
          onSubmit={handleRevisionConfirm}
          isSubmitting={isRevisioning}
          namespace="LeaveApplicationsPage"
          translationPrefix="review"
        />
      )}

      <ModalForm icon={DocumentText1} onClose={onClose} resize={false}footer={renderFooter()}>
        <div className="flex flex-col gap-6 h-full min-h-0">
          {isLoading || !currentLeave ? (
            <>
              <div className="flex flex-col gap-2 pb-5 border-b surface-base-stroke shrink-0">
                <Skeleton height={28} width={240} />
              </div>
              <div className="flex-1">
                <div className="flex flex-col py-4">
                  <div className="flex flex-col gap-2 mb-4 border-b surface-base-stroke pb-3">
                    <Skeleton height={24} width={200} />
                    <Skeleton height={32} width={150} />
                  </div>
                  <div className="flex flex-col gap-3">
                    {Array.from({ length: 5 }).map(function (_, idx) {
                      return (
                        <div key={idx} className="py-3 border-b surface-base-stroke">
                          <Skeleton height={20} width="100%" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <Skeleton height={44} width="100%" />
            </>
          ) : (
            renderLeaveContent(currentLeave)
          )}
        </div>
      </ModalForm>
    </>
  );
}
